import { useState } from 'react';
import { api } from '@/api/client';

export interface EmployerQuestion {
  id: string;
  dimension: string;
  dimension_label: string;
  question: string;
  type: string; // scale | choice | text
  scale_left?: string | null;
  scale_right?: string | null;
  options?: { value: string; label: string }[] | null;
  max_length?: number | null;
}

interface Profile {
  id: string;
  name: string;
  answers: Record<string, string | number>;
  ai_summary?: string | null;
}

interface CreatePortraitModalProps {
  questions: EmployerQuestion[];
  onClose: () => void;
  onSave: (profile: Profile) => void;
}

export default function CreatePortraitModal({ questions, onClose, onSave }: CreatePortraitModalProps) {
  const [name, setName] = useState('');
  const [step, setStep] = useState<'name' | 'questions'>('name');
  const [dimIndex, setDimIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [nameError, setNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // группы по измерениям
  const dimensions: { key: string; label: string; items: EmployerQuestion[] }[] = [];
  for (const q of questions) {
    let d = dimensions.find((x) => x.key === q.dimension);
    if (!d) {
      d = { key: q.dimension, label: q.dimension_label, items: [] };
      dimensions.push(d);
    }
    d.items.push(q);
  }

  const currentDim = dimensions[dimIndex];
  const answeredCount = questions.filter((q) => (answers[q.id] || '').toString().trim()).length;

  const handleNextStep = () => {
    const trimmed = name.trim();
    if (!trimmed) { setNameError('Введите имя руководителя'); return; }
    if (trimmed.length < 3) { setNameError('Имя должно быть не менее 3 символов'); return; }
    setNameError('');
    setStep('questions');
  };

  const setAnswer = (id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const profile = await api.post<Profile>('/employer-profiles', {
        name: name.trim(),
        answers,
      });
      onSave(profile);
    } catch (err) {
      alert((err as Error).message || 'Ошибка сохранения портрета');
      setSubmitting(false);
    }
  };

  const renderControl = (q: EmployerQuestion) => {
    const value = answers[q.id] || '';
    if (q.type === 'scale') {
      return (
        <div>
          <div className="flex items-center gap-2">
            {q.scale_left && <span className="text-xs text-foreground-400 w-24 text-right shrink-0">{q.scale_left}</span>}
            <div className="flex gap-1.5">
              {['1', '2', '3', '4', '5'].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAnswer(q.id, v)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    value === v
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            {q.scale_right && <span className="text-xs text-foreground-400 w-24 shrink-0">{q.scale_right}</span>}
          </div>
        </div>
      );
    }
    if (q.type === 'choice' && q.options) {
      return (
        <div className="flex flex-wrap gap-2">
          {q.options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setAnswer(q.id, o.value)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                value === o.value
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      );
    }
    return (
      <textarea
        value={value}
        onChange={(e) => setAnswer(q.id, e.target.value)}
        maxLength={q.max_length || 1000}
        rows={3}
        placeholder="Свободный ответ..."
        className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-background-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground-950">Новый портрет руководителя</h2>
            {step === 'questions' && (
              <p className="text-xs text-foreground-400 mt-0.5">
                {currentDim?.label} · блок {dimIndex + 1} из {dimensions.length} · отвечено {answeredCount}/{questions.length}
              </p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6">
          {step === 'name' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground-700 mb-1.5 block">Имя и роль руководителя</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Например: Виктор Ярутов — акционер"
                  className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
              </div>
              <p className="text-xs text-foreground-400 leading-relaxed">
                Дальше — {questions.length} вопросов о стиле управления. По ответам AI сформирует портрет,
                с которым будет сравнивать кандидатов при оценке совместимости.
              </p>
              <button
                onClick={handleNextStep}
                className="w-full py-3 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Перейти к вопросам
              </button>
            </div>
          )}

          {step === 'questions' && currentDim && (
            <div className="space-y-6">
              {currentDim.items.map((q) => (
                <div key={q.id}>
                  <p className="text-sm font-medium text-foreground-800 mb-2.5">{q.question}</p>
                  {renderControl(q)}
                </div>
              ))}

              <div className="flex items-center justify-between pt-4 border-t border-background-100">
                <button
                  onClick={() => (dimIndex === 0 ? setStep('name') : setDimIndex(dimIndex - 1))}
                  className="text-sm px-4 py-2.5 rounded-xl border border-background-200 text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-1" />
                  Назад
                </button>
                {dimIndex < dimensions.length - 1 ? (
                  <button
                    onClick={() => setDimIndex(dimIndex + 1)}
                    className="text-sm px-5 py-2.5 rounded-xl bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    Далее
                    <i className="ri-arrow-right-line ml-1" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || answeredCount === 0}
                    className="text-sm px-5 py-2.5 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                  >
                    {submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-sparkling-line" />}
                    {submitting ? 'Сохраняю...' : 'Создать портрет (AI-анализ)'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
